import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MdClose, MdOutlinePhotoCamera, MdOutlineSms } from 'react-icons/md';
import { getMyBills } from '../../../api/services/billingService';
import { submitRentEvidence } from '../../../api/services/rentalService';
import { uploadPaymentEvidencePhoto } from '../../../api/services/propertyService';
import { openAuthenticatedImageInNewTab } from '../../../utils/openAuthenticatedImage';
import { getRejectedRentEvidenceFromRental } from '../../../utils/tenantRentEvidence';
import './PayRentEvidenceModal.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getTenantAssignmentId(rental) {
  if (!rental || typeof rental !== 'object') return null;
  return (
    rental.assignmentId ??
    rental.tenantAssignmentId ??
    rental.unitAssignmentId ??
    rental.assignment?.id ??
    null
  );
}

function formatRejectedPeriod(month, year) {
  if (month == null || year == null) return null;
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) return null;
  const label = MONTH_NAMES[m - 1] || `Month ${m}`;
  return `${label} ${y}`;
}

const formatMoneyKes = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const hasCents = Math.floor(num) !== num;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `KES ${formatted}`;
};

const PayRentEvidenceModal = ({
  open,
  onClose,
  rental,
  onSubmitted,
  rejectedLeaseEvidence: rejectedFromParent,
  /** When true, success state only offers Close (e.g. already on Payments page). */
  hideSuccessPaymentsNav = false,
}) => {
  const navigate = useNavigate();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [evidenceMode, setEvidenceMode] = useState('mpesa');
  const [amountPaid, setAmountPaid] = useState('');
  const [mpesaMessage, setMpesaMessage] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [portalRoot, setPortalRoot] = useState(null);
  const [billRow, setBillRow] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState('');

  const assignmentId = getTenantAssignmentId(rental);
  const unit = rental?.unit || null;
  const monthlyRent = rental?.monthlyRent ?? unit?.monthlyRent;
  const propertyLabel = [rental?.propertyName, rental?.unitName || rental?.unitNumber || unit?.unitNumber]
    .filter(Boolean)
    .join(' · ');

  const rejectedLeaseEvidence = useMemo(() => {
    if (rejectedFromParent !== undefined) return rejectedFromParent;
    return getRejectedRentEvidenceFromRental(rental);
  }, [rejectedFromParent, rental]);
  const rejectedPeriodLabel = useMemo(
    () =>
      rejectedLeaseEvidence
        ? formatRejectedPeriod(rejectedLeaseEvidence.periodMonth, rejectedLeaseEvidence.periodYear)
        : null,
    [rejectedLeaseEvidence]
  );

  const y0 = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => y0 + 1 - i);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadBill = async () => {
      setBillLoading(true);
      setBillError('');
      try {
        const res = await getMyBills(year, month);
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const matchByAssignment = rows.find((row) => String(row?.assignmentId || '') === String(assignmentId || ''));
        setBillRow(matchByAssignment || rows[0] || null);
      } catch (err) {
        if (!cancelled) {
          setBillRow(null);
          setBillError(err?.message || 'Could not load monthly bill.');
        }
      } finally {
        if (!cancelled) setBillLoading(false);
      }
    };
    loadBill();
    return () => {
      cancelled = true;
    };
  }, [open, year, month, assignmentId]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setSuccess(false);

    const rejected = rejectedLeaseEvidence;
    if (rejected && rejected.periodYear != null && rejected.periodMonth != null) {
      setYear(rejected.periodYear);
      setMonth(rejected.periodMonth);
    } else {
      const d = new Date();
      setYear(d.getFullYear());
      setMonth(d.getMonth() + 1);
    }

    if (rejected) {
      const hasMpesa = String(rejected.prefill.mpesaMessage || '').trim() !== '';
      const hasPhoto = String(rejected.prefill.photoUrl || '').trim() !== '';
      setEvidenceMode(hasPhoto && !hasMpesa ? 'photo' : 'mpesa');
      setMpesaMessage(rejected.prefill.mpesaMessage || '');
      setPhotoUrl(rejected.prefill.photoUrl || '');
      const fromEv =
        rejected.prefill.amountPaid != null && String(rejected.prefill.amountPaid).trim() !== '';
      if (fromEv) {
        setAmountPaid(String(rejected.prefill.amountPaid));
      } else {
        const rentNum = Number(monthlyRent);
        setAmountPaid(Number.isFinite(rentNum) && rentNum > 0 ? String(Math.round(rentNum)) : '');
      }
    } else {
      setEvidenceMode('mpesa');
      setMpesaMessage('');
      setPhotoUrl('');
      const rentNum = Number(monthlyRent);
      setAmountPaid(Number.isFinite(rentNum) && rentNum > 0 ? String(Math.round(rentNum)) : '');
    }
  }, [open, rental, monthlyRent, rejectedLeaseEvidence]);

  useLayoutEffect(() => {
    if (!open) {
      setPortalRoot(null);
      return undefined;
    }
    if (typeof document === 'undefined' || !document.body) {
      return undefined;
    }
    const el = document.createElement('div');
    el.setAttribute('data-pay-rent-modal-portal', '');
    document.body.appendChild(el);
    setPortalRoot(el);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      setPortalRoot(null);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !success) return undefined;
    const id = window.setTimeout(() => {
      onClose();
    }, 1000);
    return () => window.clearTimeout(id);
  }, [open, success, onClose]);

  const handlePreviewAttachedPhoto = async (e) => {
    e.preventDefault();
    const u = (photoUrl || '').trim();
    if (!u) return;
    setError('');
    try {
      await openAuthenticatedImageInNewTab(u);
    } catch (err) {
      setError(err?.message || 'Could not open image.');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setError('');
      setUploading(true);
      const res = await uploadPaymentEvidencePhoto(file);
      const url = res?.data?.url || res?.data || '';
      setPhotoUrl(typeof url === 'string' ? url : '');
    } catch (err) {
      setError(err?.message || 'Failed to upload photo.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!assignmentId) {
      setError('Lease information is missing. Refresh the page or contact support.');
      return;
    }
    const amt = Number(amountPaid);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter a valid amount paid.');
      return;
    }
    const msg = (mpesaMessage || '').trim();
    const photo = (photoUrl || '').trim();
    if (evidenceMode === 'mpesa' && !msg) {
      setError('Paste your M-Pesa confirmation SMS.');
      return;
    }
    if (evidenceMode === 'photo' && !photo) {
      setError('Upload a photo of your payment receipt.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await submitRentEvidence({
        assignmentId,
        year,
        month,
        amountPaid: amt,
        mpesaMessage: evidenceMode === 'mpesa' ? msg : '',
        photoUrl: evidenceMode === 'photo' ? photo : '',
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      setError(err?.message || 'Could not submit evidence.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !portalRoot) return null;

  return createPortal(
    <div className="pay-rent-modal" role="presentation">
      <button type="button" className="pay-rent-modal__backdrop" aria-label="Close" onClick={onClose} />
      <div
        className="pay-rent-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pay-rent-modal-title"
      >
        <div className="pay-rent-modal__header">
          <h2 id="pay-rent-modal-title" className="pay-rent-modal__title">
            {rejectedLeaseEvidence ? 'Resubmit rent payment' : 'Submit rent payment'}
          </h2>
          <div className="pay-rent-modal__header-actions">
            <button type="button" className="pay-rent-modal__close" onClick={onClose} aria-label="Close">
              <MdClose />
            </button>
          </div>
        </div>

        {success ? (
          <div className="pay-rent-modal__success">
            <p className="pay-rent-modal__success-text">
              Your payment evidence was submitted. Your landlord or property agent will review and approve it. If you
              paid more than rent, the extra may be credited after approval.
            </p>
            <p className="pay-rent-modal__success-dismiss-hint">Closing…</p>
            <div className="pay-rent-modal__actions pay-rent-modal__actions--success">
              <button type="button" className="pay-rent-modal__btn pay-rent-modal__btn--secondary" onClick={onClose}>
                Close now
              </button>
              {!hideSuccessPaymentsNav ? (
                <button
                  type="button"
                  className="pay-rent-modal__btn pay-rent-modal__btn--primary"
                  onClick={() => {
                    onClose();
                    navigate('/tenant/payment-history');
                  }}
                >
                  Payments
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <form className="pay-rent-modal__form" onSubmit={handleSubmit}>
            {propertyLabel ? (
              <p className="pay-rent-modal__property">{propertyLabel}</p>
            ) : null}

            {rejectedLeaseEvidence ? (
              <div className="pay-rent-modal__rejection" role="status">
                <div className="pay-rent-modal__rejection-title">Previous evidence was rejected</div>
                {rejectedPeriodLabel ? (
                  <p className="pay-rent-modal__rejection-period">Period: {rejectedPeriodLabel}</p>
                ) : null}
                {rejectedLeaseEvidence.rejectionReason ? (
                  <div className="pay-rent-modal__rejection-reason">
                    <span className="pay-rent-modal__rejection-reason-label">Reason from landlord or agent</span>
                    <p className="pay-rent-modal__rejection-reason-text">{rejectedLeaseEvidence.rejectionReason}</p>
                  </div>
                ) : (
                  <p className="pay-rent-modal__rejection-hint">
                    Send updated M-Pesa message or receipt photo for the period you select below.
                  </p>
                )}
              </div>
            ) : null}

            {!assignmentId ? (
              <p className="pay-rent-modal__warn">
                We couldn&apos;t load your lease reference. Try refreshing the page or contact support.
              </p>
            ) : null}

            <div className="pay-rent-modal__field">
              <span className="pay-rent-modal__label">Period</span>
              <div className="pay-rent-modal__period-row">
                <label className="visually-hidden" htmlFor="pay-rent-month">Month</label>
                <select
                  id="pay-rent-month"
                  className="pay-rent-modal__select"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
                <label className="visually-hidden" htmlFor="pay-rent-year">Year</label>
                <select
                  id="pay-rent-year"
                  className="pay-rent-modal__select"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pay-rent-modal__bill-box" aria-live="polite">
              <div className="pay-rent-modal__bill-title">What to pay for this month</div>
              {billLoading ? (
                <p className="pay-rent-modal__bill-state">Loading bill...</p>
              ) : billError ? (
                <p className="pay-rent-modal__bill-state pay-rent-modal__bill-state--error">{billError}</p>
              ) : billRow ? (
                <div className="pay-rent-modal__bill-grid">
                  <div>
                    <span>Rent</span>
                    <strong>{formatMoneyKes(billRow.rentAmount)}</strong>
                  </div>
                  <div>
                    <span>Water</span>
                    <strong>{formatMoneyKes(billRow.waterAmount)}</strong>
                  </div>
                  <div>
                    <span>Water units</span>
                    <strong>
                      {Number.isFinite(Number(billRow.waterUnits)) ? Number(billRow.waterUnits).toLocaleString() : '—'}
                    </strong>
                  </div>
                  <div>
                    <span>Total due</span>
                    <strong>{formatMoneyKes(billRow.totalDue)}</strong>
                  </div>
                  <div>
                    <span>Remaining due</span>
                    <strong>{formatMoneyKes(billRow.remainingDue)}</strong>
                  </div>
                </div>
              ) : (
                <p className="pay-rent-modal__bill-state">No bill found for this period.</p>
              )}
            </div>

            <div className="pay-rent-modal__field">
              <label className="pay-rent-modal__label" htmlFor="pay-rent-amount">
                Amount paid (KES)
              </label>
              <input
                id="pay-rent-amount"
                type="number"
                className="pay-rent-modal__input"
                min="1"
                step="1"
                inputMode="numeric"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                placeholder="e.g. 30000"
              />
            </div>

            <div className="pay-rent-modal__field">
              <span className="pay-rent-modal__label">Evidence</span>
              <div className="pay-rent-modal__tabs" role="tablist" aria-label="Evidence type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={evidenceMode === 'mpesa'}
                  className={`pay-rent-modal__tab${evidenceMode === 'mpesa' ? ' pay-rent-modal__tab--active' : ''}`}
                  onClick={() => setEvidenceMode('mpesa')}
                >
                  <MdOutlineSms aria-hidden />
                  M-Pesa SMS
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={evidenceMode === 'photo'}
                  className={`pay-rent-modal__tab${evidenceMode === 'photo' ? ' pay-rent-modal__tab--active' : ''}`}
                  onClick={() => setEvidenceMode('photo')}
                >
                  <MdOutlinePhotoCamera aria-hidden />
                  Photo
                </button>
              </div>
            </div>

            {evidenceMode === 'mpesa' ? (
              <div className="pay-rent-modal__field">
                <label className="pay-rent-modal__label" htmlFor="pay-rent-mpesa">
                  Paste confirmation message
                </label>
                <textarea
                  id="pay-rent-mpesa"
                  className="pay-rent-modal__textarea"
                  rows={5}
                  value={mpesaMessage}
                  onChange={(e) => setMpesaMessage(e.target.value)}
                  placeholder="Confirmed. Ksh… sent to…"
                />
              </div>
            ) : (
              <div className="pay-rent-modal__field">
                <label className="pay-rent-modal__label" htmlFor="pay-rent-file">
                  Receipt photo
                </label>
                <div className="pay-rent-modal__upload">
                  <input
                    id="pay-rent-file"
                    type="file"
                    accept="image/*"
                    className="pay-rent-modal__file-input"
                    onChange={handleFile}
                    disabled={uploading}
                  />
                  <label htmlFor="pay-rent-file" className="pay-rent-modal__file-label">
                    {uploading ? 'Uploading…' : photoUrl ? 'Replace photo' : 'Choose image'}
                  </label>
                  {photoUrl ? (
                    <>
                      <span className="pay-rent-modal__file-ok">Image attached</span>
                      <button
                        type="button"
                        className="pay-rent-modal__preview-photo"
                        onClick={handlePreviewAttachedPhoto}
                      >
                        View image
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            <p className="pay-rent-modal__hint">
              Provide either an M-Pesa SMS or a photo. Overpayments may be carried forward as credit after approval.
            </p>

            {error ? <p className="pay-rent-modal__error">{error}</p> : null}

            <div className="pay-rent-modal__actions">
              <button type="button" className="pay-rent-modal__btn pay-rent-modal__btn--secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="pay-rent-modal__btn pay-rent-modal__btn--primary"
                disabled={submitting || !assignmentId || uploading}
              >
                {submitting ? 'Submitting…' : 'Submit evidence'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    portalRoot
  );
};

export default PayRentEvidenceModal;
